import React from 'react';
import { Link } from 'react-router-dom';
import { Plane, Linkedin, Instagram, Mail, Phone, MapPin } from 'lucide-react';

export function Footer() {
  const navigation = {
    services: [
      { name: 'Ground Transportation', href: '/airport/jfk' },
      { name: 'Maintenance & Detailing', href: '/airport/jfk' },
      { name: 'Restaurants & Catering', href: '/airport/jfk' },
      { name: 'FBOs', href: '/airport/jfk' },
    ],
    company: [
      { name: 'Pricing', href: '/pricing' },
      { name: 'List Your Service', href: '/signup' },
      { name: 'Sign in', href: '/signin' },
      { name: 'Track Aircraft', href: '/tracking' },
    ],
    legal: [
      { name: 'Legal', href: '/privacy' },
      { name: 'Terms of Service', href: '/terms' },
      { name: 'Cookie Policy', href: '/cookies' },
    ],
    social: [
      { name: 'LinkedIn', icon: Linkedin, href: '#' },
      { name: 'Instagram', icon: Instagram, href: '#' },
    ],
  };

  return (
    <footer className="relative bg-slate-900 mt-auto overflow-hidden" aria-labelledby="footer-heading">
      <h2 id="footer-heading" className="sr-only">Footer</h2>
      
      {/* Background elements */}
      <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900"></div>
      <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1436491865332-7a61a109cc05?auto=format&fit=crop&w=2000')] bg-cover bg-center opacity-5"></div>
      
      <div className="relative z-10 max-w-7xl mx-auto py-16 px-4 sm:px-6 lg:py-20 lg:px-8">
        <div className="xl:grid xl:grid-cols-3 xl:gap-12">
          {/* Brand section */}
          <div className="space-y-8 xl:col-span-1">
            <Link to="/" className="flex items-center gap-3 group">
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-r from-primary-400 to-blue-500 rounded-xl blur-lg opacity-60 group-hover:opacity-80 transition-opacity duration-300"></div>
                <div className="relative bg-gradient-to-r from-primary-500 to-blue-600 p-3 rounded-xl shadow-lg">
                  <Plane className="h-8 w-8 text-white" />
                </div>
              </div>
              <div>
                <span className="text-3xl font-bold bg-gradient-to-r from-white to-primary-200 bg-clip-text text-transparent">
                  AirPortal
                </span>
                <div className="text-sm text-gray-400 -mt-1">Aviation Services Marketplace</div>
              </div>
            </Link>
            
            <p className="text-gray-300 text-base leading-relaxed max-w-md">
              Connecting aviation service providers with clients worldwide. Streamline your operations and grow your business with our comprehensive platform.
            </p>
            
            {/* Contact info */}
            <div className="space-y-3">
              <div className="flex items-center gap-3 text-gray-300">
                <Mail size={18} className="text-primary-400" />
                <span>contact@airportals.net</span>
              </div>
              <div className="flex items-center gap-3 text-gray-300">
                <Phone size={18} className="text-primary-400" />
                <span>+1 (555) 123-4567</span>
              </div>
              <div className="flex items-center gap-3 text-gray-300">
                <MapPin size={18} className="text-primary-400" />
                <span>Global Operations</span>
              </div>
            </div>
            
            {/* Social links and Language Selector */}
            <div className="flex items-center justify-between">
              <div className="flex space-x-4">
                {navigation.social.map((item) => (
                  <a
                    key={item.name}
                    href={item.href}
                    className="group p-3 bg-white/5 rounded-xl hover:bg-white/10 transition-all duration-300 hover:scale-110"
                  >
                    <span className="sr-only">{item.name}</span>
                    <item.icon className="h-6 w-6 text-gray-400 group-hover:text-primary-400 transition-colors duration-300" />
                  </a>
                ))}
              </div>
            </div>
          </div>
          
          {/* Navigation sections */}
          <div className="mt-16 grid grid-cols-2 gap-8 xl:mt-0 xl:col-span-2">
            <div className="md:grid md:grid-cols-2 md:gap-8">
              <div>
                <h3 className="text-lg font-semibold text-white mb-6">
                  Services
                </h3>
                <ul className="space-y-4">
                  {navigation.services.map((item) => (
                    <li key={item.name}>
                      <Link 
                        to={item.href} 
                        className="text-gray-300 hover:text-primary-300 transition-colors duration-200 hover:translate-x-1 inline-block"
                      >
                        {item.name}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
              
              <div className="mt-12 md:mt-0">
                <h3 className="text-lg font-semibold text-white mb-6">
                  Company
                </h3>
                <ul className="space-y-4">
                  {navigation.company.map((item) => (
                    <li key={item.name}>
                      <Link 
                        to={item.href} 
                        className="text-gray-300 hover:text-primary-300 transition-colors duration-200 hover:translate-x-1 inline-block"
                      >
                        {item.name}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            
            <div>
              <h3 className="text-lg font-semibold text-white mb-6">
                Legal
              </h3>
              <ul className="space-y-4">
                {navigation.legal.map((item) => (
                  <li key={item.name}>
                    <Link 
                      to={item.href} 
                      className="text-gray-300 hover:text-primary-300 transition-colors duration-200 hover:translate-x-1 inline-block"
                    >
                      {item.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
        
        {/* Bottom section */}
        <div className="mt-16 pt-8 border-t border-gray-800">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-gray-400 text-center md:text-left">
              &copy; {new Date().getFullYear()} AirPortal. All rights reserved.
            </p>
            
            <div className="flex items-center gap-6 text-sm text-gray-400">
              <span>Made with ❤️ for Aviation</span>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                <span>All systems operational</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}